import { useEffect } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import { updateSEO, seoData } from '../../../utils/seo';

// ===== 執事とAIの役割分担（route=/standards/ai-role）=====
// デザイン土台: 紺#1a2942 / 真鍮金#a38e65 / BTN-08 / 紺帯番号バンド / 白リッチカード / 細罫エディトリアル行
// /standards/certification 等と同型セクションの使い回しはしない（構成を変えた新バリエーション）
// 事実正典: 創立1890年（明治23年）/ 法人設立2025年2月19日 / 代表理事 石井裕一（三代目頭取）/
//   資格は初級Associate・中級Professional・上級Masterの3段階+専門資格 /
//   協会と執事とお客様の間で双方向の守秘義務契約を結ぶ
// 創作した年号・数値・実績・具体的なAI製品名やサービス名は書かない

export default function StandardsAiRolePage() {
  useEffect(() => {
    updateSEO(seoData['/standards/ai-role']);
  }, []);

  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: '基準・指針', href: '/standards' },
    { label: '執事とAIの役割分担' }
  ];

  const BTN08_ON_LIGHT =
    "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
  const BTN08_ON_NAVY =
    "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  const whyAxes = [
    {
      en: 'Boundary',
      ja: '境界を先に置く',
      text: '便利さの速度に押されると、どこまでを道具に委ね、どこからを人が引き受けるかが曖昧になります。基準は、現場の忙しさのなかでも境界を見失わないための座標です。'
    },
    {
      en: 'Dignity',
      ja: '品位を守る',
      text: '執事の仕事は速さの競い合いではありません。依頼者の尊厳と静けさを守る手つきが先にあり、技術はその手つきを支える補助線にとどめます。'
    },
    {
      en: 'Accountability',
      ja: '責任の所在を明確にする',
      text: '生成された文面や要約があっても、最終的に差し出す判断の責任は人に残ります。役割分担の基準化は、責任を道具へ逃がさないための手続きです。'
    }
  ];

  const aiDomains = [
    {
      title: '情報整理',
      text: '公開情報や、すでに許可された社内資料の見出し・時系列・対照表への並べ替え。散らばった断片を一覧にし、人が判断しやすい形へ整える作業です。'
    },
    {
      title: '下調べ',
      text: '一般に入手できる制度・地理・季節行事などの基礎確認。出典の所在を残し、断定せず「要人確認」の印を付けたまま人へ渡すことが条件です。'
    },
    {
      title: '文面の下書き',
      text: '挨拶文や案内の骨格、言い回しの候補出し。トーンの最終調整、宛名の正確さ、関係性に応じた距離感は、必ず人が手を入れます。'
    },
    {
      title: '記録の要約',
      text: '長文の議事や申し送りを、判断に必要な事実の箇条へ圧縮する補助。感情の解釈や評価語の付加は行わず、要約後は原文との突合を人が行います。'
    }
  ];

  const humanDomains = [
    {
      no: '01',
      title: '判断の責任',
      text: '何を優先し、何を止め、何を依頼者へ返すか。安全・尊厳・約束の時刻が交錯する場面での決定は、人が引き受け、説明できる形で残します。'
    },
    {
      no: '02',
      title: '感情への応対',
      text: '不安・怒り・沈黙の奥にある要請を受け止める応対は、型だけでは足りません。相手の呼吸に合わせ、言葉の長さと間を選ぶのは人の領域です。'
    },
    {
      no: '03',
      title: '場の空気の読み取り',
      text: '来客の流れ、季節、邸内の動線、いま話すべきでない話題。数値化されにくい気配を読み、先回りの一手を置くか控えるかを決めます。'
    },
    {
      no: '04',
      title: '最終確認',
      text: '差し出す前の一文、手配の直前、取り消せない予約の手前。生成物や下調べの結果を含め、事実・宛先・禁忌の最終確認は人が行います。'
    }
  ];

  const noInputRules = [
    {
      label: '固有の識別情報',
      text: '氏名、住所、連絡先、家族構成、勤務先、渡航予定など、個人を特定しうる情報は、道具側へ入力しません。必要なら仮名と一般化した条件だけを用います。'
    },
    {
      label: '嗜好と禁忌の生データ',
      text: '食の禁忌、健康上の配慮、信仰、人間関係の機微は、家の信頼そのものです。要約や下書きの便宜のために外部へ出さないのが原則です。'
    },
    {
      label: '契約と守秘の範囲',
      text: '当協会は、協会と執事とお客様のあいだで双方向の守秘義務契約を結びます。契約上の秘密を、便利さのために道具へ渡すことは、役割分担の前提を壊します。'
    }
  ];

  const verifySteps = [
    {
      en: 'Source',
      ja: '出典を辿る',
      text: '下調べの一文ごとに、根拠となる公開情報や許可済み資料へ戻れるかを確認します。辿れない断定は、現場に出しません。'
    },
    {
      en: 'Tone',
      ja: '距離感を整える',
      text: '下書きの丁寧さが、その家の関係性に合っているか。過剰な賛辞や、事務的すぎる短さがないかを人が調整します。'
    },
    {
      en: 'Fact',
      ja: '事実を突合する',
      text: '日時、固有名詞、数量、手順の前後関係。要約が落とした例外や条件がないかを、原文または一次情報と照合します。'
    },
    {
      en: 'Stop',
      ja: '止める判断',
      text: '確信が持てない生成物は使わず、人手で書き直すか、確認が取れるまで差し出しを延期します。速さより、誤りを外に出さない静けさを選びます。'
    }
  ];

  const consentPoints = [
    {
      title: '用いる範囲の明示',
      text: 'どの作業に技術を補助として用いるか——情報整理、下調べ、下書き、要約——を、曖昧な「効率化」ではなく具体の工程で説明します。'
    },
    {
      title: '入れないものの約束',
      text: '依頼者に関する固有情報や機微を道具へ入力しないこと、生成物は必ず人が検証してから用いることを、先に共有します。'
    },
    {
      title: '拒否と変更の余地',
      text: '技術の補助を望まない工程があれば、その意思を尊重します。一度の同意が、将来のすべての手段を包括するわけではないことも伝えます。'
    }
  ];

  const enduringStandards = [
    {
      en: 'Person First',
      ja: '人が責任の主体',
      text: '道具の性能が上がっても、差し出す判断と説明の主体は人です。責任をモデルや画面の向こうへ移さないことが、奉仕の下限です。'
    },
    {
      en: 'Secrecy',
      ja: '秘密は外に出さない',
      text: '守秘は手段の新旧で揺れません。双方向の守秘義務契約のもと、依頼者の情報を必要のない回路へ乗せない原則は変わりません。'
    },
    {
      en: 'Verification',
      ja: '検証なき出力は使わない',
      text: '生成の速度が上がるほど、人の突合と停止判断の価値は増します。未検証のまま現場へ出すことは、どの時代でも基準外です。'
    },
    {
      en: 'Consent',
      ja: '説明と同意を先に置く',
      text: '見えにくい補助ほど、用いる範囲と入れないものを言葉にして共有します。沈黙のままの導入は、信頼の手続きを欠きます。'
    }
  ];

  const faqItems = [
    {
      q: 'AIに任せてよい作業と、任せてはならない作業の境目はどこですか？',
      a: '境目は「判断・感情・空気・最終確認」に人が残るかどうかです。情報整理・下調べ・文面の下書き・記録の要約は、条件付きで補助に用い得ます。一方、優先順位の決定、感情への応対、場の空気の読み取り、差し出し前の最終確認は人の領域です。便利さより、責任の所在が説明できるかを基準にします。'
    },
    {
      q: '依頼者の情報を一切入力しないと、実務で使えなくなりませんか？',
      a: '固有の識別情報や嗜好・禁忌の生データを入れないことが原則です。一般化した条件や、すでに公開されている情報、許可された範囲の社内向け整理に限定すれば、補助としての整理や下書きは可能です。使えなくなることを恐れて秘密を外へ出すより、手作業に戻る判断を品位とみなします。'
    },
    {
      q: '技術が変わったとき、この基準ごと更新するのですか？',
      a: '手順や画面の操作は更新され得ますが、人が責任の主体であること、秘密を不要な回路へ乗せないこと、検証なき出力を使わないこと、説明と同意を先に置くことは、技術の世代が変わっても維持する座標です。新しい手段は、この座標に載る範囲でのみ採用します。'
    }
  ];

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: '執事とAIの役割分担｜全日本執事協会が定める技術利用の基準',
    description:
      '全日本執事協会が定める、執事業務におけるAIとの役割分担の基準。任せてよい領域と人が担う領域、依頼者情報を入力しない原則、生成物の検証、説明と同意、技術が変わっても変わらない座標。',
    author: {
      '@type': 'Organization',
      name: '一般社団法人 全日本執事協会'
    },
    publisher: {
      '@type': 'Organization',
      name: '一般社団法人 全日本執事協会'
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://japanbutlerassociation.com/standards/ai-role'
    }
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'ホーム', item: 'https://japanbutlerassociation.com/' },
      { '@type': 'ListItem', position: 2, name: '基準・指針', item: 'https://japanbutlerassociation.com/standards' },
      { '@type': 'ListItem', position: 3, name: '執事とAIの役割分担', item: 'https://japanbutlerassociation.com/standards/ai-role' }
    ]
  };

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a
      }
    }))
  };

  return (
    <Layout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} />

      {/* ヒーロー — 全幅ビルボード */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[520px] lg:min-h-[600px] flex items-center">
        <img
              src="/images/gen2/std2-ai-human-judgment.jpg"
              alt="デジタル画面を前に技術と向き合い人の判断を保つ執事"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-[linear-gradient(100deg,rgba(16,20,26,0.93)_0%,rgba(16,20,26,0.7)_50%,rgba(16,20,26,0.32)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[720px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">
              AI Role Boundaries
            </span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(28px,3.4vw,44px)] leading-[1.4] tracking-[0.04em] mb-6">
              <span className="block">執事とAIの役割分担</span>
              <span className="block text-[#d7b779]">技術を用いるうえでの協会基準</span>
            </h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              一般社団法人 全日本執事協会は、1890年（明治23年）の創立以来、日本における執事文化の継承に取り組んできました。代表理事・石井裕一（三代目頭取）のもと、本頁は認定団体である当協会が定める、技術を用いるうえでの役割分担の基準です。任せてよい領域と人が担う領域、依頼者の情報を入力しない原則、生成物をそのまま使わない原則、説明と同意、そして技術が変わっても変わらない座標を、同じ品位のうえで開きます。
            </p>
          </div>
        </div>
      </section>

      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <div className="bg-white">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-20 space-y-20 sm:space-y-24">

          {/* S1. 基準化する理由 — 中央リード＋下に三枚縦ストライプ（certificationの横三枚カードとは別） */}
          <section>
            <div className="text-center mb-10">
              <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
                I. Why Define the Boundary
              </span>
              <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
                役割分担を<span className="text-[#a38e65]">基準化する理由</span>
              </h2>
              <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mx-auto">
                新しい道具は、手順を短く見せます。しかし執事の現場では、短いことが常に正しいとは限りません。何を補助に委ね、何を人の手に残すかを先に言葉にしておくこと——それが、依頼者の静けさと、担い手自身の自制を守るための基準化です。
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6 mb-10">
              {whyAxes.map((item, idx) => (
                <div
                  key={item.en}
                  className="relative bg-[#fbfaf7] border border-[#e2ddd2] p-7 sm:p-8 pt-10"
                >
                  <span className="absolute top-0 left-7 -translate-y-1/2 font-['Playfair_Display',serif] text-[#a38e65] text-sm tracking-[0.14em] bg-white px-2 border border-[#e2ddd2]">
                    0{idx + 1}
                  </span>
                  <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[12px] tracking-[0.08em] mb-2">{item.en}</span>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-3">{item.ja}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-[14px] text-[#4a4638] leading-[1.9]">{item.text}</p>
                </div>
              ))}
            </div>
            <div className="grid lg:grid-cols-[1fr_280px] gap-8 items-center">
              <div className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] space-y-[18px]">
                <p>
                  本基準は、特定の製品やサービスを推奨・排除するための一覧ではありません。初級Associate・中級Professional・上級Masterの三段階と専門資格を通じて培う観察・自制・接遇・段取りの芯を、技術利用の場面でも揺らさないための指針です。
                </p>
                <p>
                  情報の扱いに関する隣接する定めは
                  <a href="/standards/infosec" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">情報セキュリティ</a>
                  と
                  <a href="/standards/confidentiality" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">守秘義務</a>
                  に詳述しています。本頁は「誰が、何を、どこまで担うか」に焦点を当てます。
                </p>
              </div>
              <img
                src="/images/generated/jba-std-ai-role-02.jpg"
                alt="役割の境界を共有する協会内の打ち合わせ"
                className="w-full h-52 object-cover border border-[#a38e65]/30"
              />
            </div>
          </section>

          {/* S2. AIに任せてよい領域 — 左紺レール＋2×2カード */}
          <section>
            <div className="grid lg:grid-cols-[8px_1fr] gap-0 mb-8">
              <div className="hidden lg:block bg-[#a38e65]"></div>
              <div className="bg-white border border-[#e2ddd2] px-6 sm:px-10 py-8 sm:py-10">
                <div className="flex flex-wrap items-baseline gap-x-4 gap-y-2 mb-4">
                  <span className="font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56]">
                    II. Permitted Assistance
                  </span>
                  <span className="font-['Playfair_Display',serif] text-[#a38e65] text-sm tracking-[0.12em]">補助の範囲</span>
                </div>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
                  AIに任せてよい領域
                </h2>
                <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
                  任せてよいのは、判断を代替する作業ではなく、人が判断しやすくするための下ごしらえです。情報整理・下調べ・文面の下書き・記録の要約の四つに限り、かつ依頼者の固有情報を乗せないこと、出力を人が検証することを条件とします。
                </p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-0 border border-[#1a2942] mb-8">
              {aiDomains.map((item, idx) => (
                <div
                  key={item.title}
                  className={`p-7 sm:p-8 bg-white ${
                    idx % 2 === 0 ? 'sm:border-r border-[#e2ddd2]' : ''
                  } ${
                    idx < 2 ? 'border-b border-[#e2ddd2]' : ''
                  }`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span className="font-['Playfair_Display',serif] text-[#d7b779] text-xl">0{idx + 1}</span>
                    <h3 className="jba-display font-medium text-[#1a2942] text-lg">{item.title}</h3>
                  </div>
                  <p className="font-['Noto_Serif_JP',serif] text-[14px] text-[#4a4638] leading-[1.9]">{item.text}</p>
                </div>
              ))}
            </div>
            <div className="bg-[#1a2942] px-6 sm:px-8 py-5 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8">
              <span className="font-['Playfair_Display',serif] text-[#d7b779] tracking-[0.18em] text-sm shrink-0">
                PREPARE · DO NOT DECIDE
              </span>
              <p className="font-['Noto_Serif_JP',serif] text-sm text-[#e7e2d8] leading-[1.9]">
                補助の良さは、人が考える時間を奪うことではなく、確認すべき点を短く並べ、判断の入口を整えることにあります。
              </p>
            </div>
          </section>

          {/* S3. 人が担う領域 — 上画像フル幅＋番号付き縦リスト */}
          <section>
            <div className="mb-8">
              <img
              src="/images/gen2/std2-ai-human-response.jpg"
              alt="電話と書面を確認し判断と応対を人が引き受ける執事"
                className="w-full h-56 sm:h-64 object-cover border border-[#a38e65]/30"
              />
            </div>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              III. Human Responsibility
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              人が担う領域
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8 max-w-3xl">
              人が担う領域は、道具が苦手な作業の残りものではありません。奉仕の中核——責任、感情、空気、最終確認——を、意図して人の側に残す設計です。ここを自動化の対象に見ると、表面は整っても信頼の芯が薄れます。
            </p>
            <div className="space-y-0 border-t border-[#1a2942] mb-8">
              {humanDomains.map((item, idx) => (
                <div
                  key={item.no}
                  className={`grid sm:grid-cols-[72px_1fr] gap-4 sm:gap-6 py-6 ${
                    idx < humanDomains.length - 1 ? 'border-b border-[#e2ddd2]' : ''
                  }`}
                >
                  <span className="font-['Playfair_Display',serif] text-[#a38e65] text-2xl">{item.no}</span>
                  <div>
                    <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2">{item.title}</h3>
                    <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.9]">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-[#fbfaf7] border-l-2 border-[#a38e65] px-6 sm:px-8 py-6">
              <p className="font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#333] leading-[1.95]">
                資格制度における段階——型を身につけるAssociate、判断の再現を確かめるProfessional、示範と設計へ進むMaster——も、いずれも「人が引き受ける力」の深まりを見ます。技術の補助は、その深まりを省略する近道にはなりません。学びの枠組みは
                <a href="/qualifications" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">資格制度</a>
                を参照してください。
              </p>
            </div>
          </section>

          {/* S4. 入力しない原則 — 紺ヘッダ＋左リスト右画像 */}
          <section>
            <div className="bg-[#1a2942] px-6 sm:px-10 py-8 sm:py-10 mb-0">
              <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#d7b779] mb-3">
                IV. No Client Data Input
              </span>
              <h2 className="jba-display font-medium text-white text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-4">
                依頼者の情報をAIに入力しない原則
              </h2>
              <p className="font-['Noto_Serif_JP',serif] text-base text-[#e7e2d8] leading-[1.95] max-w-3xl">
                便利さのための入力は、秘密の移動です。当協会は、協会と執事とお客様のあいだで双方向の守秘義務契約を結びます。契約の精神に照らせば、依頼者に関する固有情報や機微を、下書きや要約の材料として道具側へ渡すことは原則として行いません。
              </p>
            </div>
            <div className="grid lg:grid-cols-[1fr_300px] gap-0 border border-t-0 border-[#e2ddd2] bg-white mb-8">
              <div className="divide-y divide-[#e2ddd2]">
                {noInputRules.map((item, idx) => (
                  <div key={item.label} className="px-6 sm:px-8 py-6">
                    <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2 flex items-center gap-3">
                      <span className="text-[#a38e65] font-['Playfair_Display',serif] text-sm tracking-[0.12em] shrink-0">
                        0{idx + 1}
                      </span>
                      <span>{item.label}</span>
                    </h3>
                    <p className="font-['Noto_Serif_JP',serif] text-[14px] text-[#4a4638] leading-[1.9] pl-9">{item.text}</p>
                  </div>
                ))}
              </div>
              <div className="border-t lg:border-t-0 lg:border-l border-[#e2ddd2] p-6 flex flex-col justify-center gap-6">
                <img
                  src="/images/generated/jba-std-ai-role-04.jpg"
                  alt="依頼者情報の取扱いを点検する静かな確認作業"
                  className="w-full h-44 object-cover border border-[#a38e65]/30"
                />
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">
                  迷ったときは入力しない。一般化した条件でも足りない場合は、技術を使わず人手で進めます。守秘の詳細は守秘義務の頁と読み合わせてください。
                </p>
              </div>
            </div>
          </section>

          {/* S5. 生成物をそのまま使わない — 四枚検証カード */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              V. Human Verification Required
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              生成物をそのまま使わない原則
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8 max-w-3xl">
              下書きや要約は、完成品ではありません。そのまま送信する、そのまま手配に使う、そのまま記録台帳へ転記する——いずれも基準外です。必ず人が検証し、必要なら書き直し、確信が持てなければ差し出しを止めます。
            </p>
            <div className="grid sm:grid-cols-2 gap-6 mb-8">
              {verifySteps.map((item) => (
                <div key={item.en} className="bg-white border border-[#e2ddd2] shadow-[0_12px_40px_rgba(26,41,66,0.06)] p-7">
                  <div className="flex items-baseline gap-3 mb-3">
                    <span className="font-['Playfair_Display',serif] italic text-[#8f7a56] text-[12px] tracking-[0.08em]">{item.en}</span>
                    <h3 className="jba-display font-medium text-[#1a2942] text-lg">{item.ja}</h3>
                  </div>
                  <div className="w-8 h-px bg-[#a38e65] mb-4"></div>
                  <p className="font-['Noto_Serif_JP',serif] text-[14px] text-[#333] leading-[1.9]">{item.text}</p>
                </div>
              ))}
            </div>
            <div className="grid lg:grid-cols-[300px_1fr] gap-8 items-center">
              <img
              src="/images/gen2/std2-ai-output-review.jpg"
              alt="AIの生成物を画面と資料で照合し人が検証して手直しする確認場面"
                className="w-full h-48 object-cover border border-[#a38e65]/30"
              />
              <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
                検証は、あら探しの儀式ではありません。出典・距離感・事実・停止判断の四点を短い手順として身体に入れ、緊張下でも省略しないことが目的です。基礎の型を疎かにしたまま高度な補助だけを重ねると、誤りは静かに外へ出ます。日々の確認の手つきこそが、技術利用の安全弁です。
              </p>
            </div>
          </section>

          {/* S6. 説明と同意 — 中央見出し＋三枚上罫 */}
          <section>
            <div className="text-center mb-10">
              <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
                VI. Explanation and Consent
              </span>
              <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
                依頼者への説明と同意
              </h2>
              <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mx-auto">
                見えにくい補助ほど、先に言葉にします。どの工程で技術を用いるか、何を入力しないか、生成物を人が検証するか——この三点を共有し、望まない工程があれば手作業へ戻る余地を残します。
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {consentPoints.map((item) => (
                <div key={item.title} className="border-t-2 border-[#1a2942] pt-5">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-3">{item.title}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-[14px] text-[#4a4638] leading-[1.9]">{item.text}</p>
                </div>
              ))}
            </div>
            <div className="bg-[#fbfaf7] border border-[#e2ddd2] px-6 sm:px-8 py-6">
              <p className="font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#333] leading-[1.95]">
                説明は、専門用語の羅列ではなく、依頼者が「自分の情報がどこへ行かないか」を想像できる粒度で行います。同意は一度きりの包括ではなく、手段や工程が変わるときの再確認を含みます。沈黙を合意とみなさないこと——それが、技術利用における礼儀です。情報面の守り方は
                <a href="/standards/infosec" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">情報セキュリティ</a>
                の基準とも接続します。
              </p>
            </div>
          </section>

          {/* S7. 変わらない基準 — 紺帯＋2×2 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              VII. Enduring Coordinates
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              技術が変わっても変わらない基準
            </h2>
            <div className="bg-[#1a2942] px-6 sm:px-8 py-4 mb-8 flex flex-wrap items-center gap-x-8 gap-y-2">
              <span className="font-['Playfair_Display',serif] text-[#d7b779] tracking-[0.18em] text-sm">BEYOND TOOLS</span>
              <span className="font-['Noto_Serif_JP',serif] text-[#f8f6f0]/85 text-sm tracking-[0.06em]">
                手段は更新されても、座標は動かさない
              </span>
            </div>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8 max-w-3xl">
              操作手順や画面の名称は、やがて古くなります。それでも、人が責任の主体であること、秘密を不要な回路へ乗せないこと、検証なき出力を使わないこと、説明と同意を先に置くこと——この四つは、技術の世代が変わっても維持する協会の座標です。
            </p>
            <div className="grid sm:grid-cols-2 gap-6 mb-8">
              {enduringStandards.map((item) => (
                <div key={item.en} className="bg-white border border-[#e2ddd2] p-7">
                  <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[12px] tracking-[0.08em] mb-2">{item.en}</span>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-3">{item.ja}</h3>
                  <div className="w-8 h-px bg-[#a38e65] mb-4"></div>
                  <p className="font-['Noto_Serif_JP',serif] text-[14px] text-[#333] leading-[1.9]">{item.text}</p>
                </div>
              ))}
            </div>
            <div className="grid lg:grid-cols-[1fr_300px] gap-8 items-center">
              <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
                新しい手段を採るときは、まずこの座標に載るかを問います。載らない速さ、載らない自動化、載らない「全部おまかせ」は、どれほど巧妙でも採用しません。1890年（明治23年）から続く系譜のうえで、代表理事・石井裕一（三代目頭取）のもと、協会が守るのは製品の流行ではなく、依頼者の尊厳と現場の静けさです。
              </p>
              <img
                src="/images/generated/jba-std-ai-role-06.jpg"
                alt="変わらない座標を後進へ伝える上級の示範"
                className="w-full h-48 object-cover border border-[#a38e65]/30"
              />
            </div>
          </section>

          {/* S8. FAQ（8本目h2）＋関連導線を内包 — 9th sectionは設けず本sectionで完結 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              VIII. Questions and Paths
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              よくある問いと、隣接する基準へ
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8 max-w-3xl">
              役割分担の基準は、禁止事項の羅列ではなく、現場で立ち返る問いの一覧です。判断に迷ったとき、次の問いと、守秘・情報・資格の頁をあわせて開いてください。
            </p>

            <div className="border-t-2 border-[#1a2942] mb-12">
              <p className="font-['Playfair_Display',serif] uppercase tracking-[0.18em] text-[12px] text-[#8f7a56] pt-6 mb-4">
                Questions
              </p>
              {faqItems.map((item) => (
                <div key={item.q} className="border-b border-[#e2ddd2] py-6">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-3 flex gap-3">
                    <span className="text-[#a38e65] font-['Playfair_Display',serif] shrink-0">Q</span>
                    <span>{item.q}</span>
                  </h3>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.95] pl-8">
                    {item.a}
                  </p>
                </div>
              ))}
            </div>

            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
              <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#d7b779] mb-4">
                Related Paths
              </span>
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                執事とAIの役割分担は、守秘義務・情報セキュリティ・資格の段階と読み合わせて初めて、日常の手つきへ落ちます。全日本執事協会は、1890年（明治23年）から続く系譜のうえで、技術の世代が変わっても、人が責任を引き受け、秘密を守り、検証と同意を先に置く基準を開き続けます。
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-4 sm:gap-6">
                <a href="/standards/confidentiality" className={BTN08_ON_NAVY}>
                  守秘義務の基準
                </a>
                <a href="/standards/infosec" className={BTN08_ON_NAVY}>
                  情報セキュリティ
                </a>
                <a href="/qualifications" className={BTN08_ON_NAVY}>
                  資格制度を見る
                </a>
              </div>
              <div className="mt-6">
                <a href="/standards" className={BTN08_ON_LIGHT}>
                  基準・指針の一覧
                </a>
              </div>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
}
